import React, {createContext, useContext, useEffect, useMemo, useReducer} from 'react';
import PropTypes from "prop-types";
import {useDispatch} from "react-redux";
import {initialState} from './actions';
import {reducer} from './CustomWorklist.reducer';
import {loadCustomFilteredList} from "../../../actions/navigation/navigationActions";

import {deleteCustomWorklist, getCustomWorklist, saveCustomWorklist as saveCustomWorklistRequest} from './api';
import {getGetters} from './getters';
import {getToggles} from './toggles';
import {getSetters} from './setters';
import {useQuery} from 'react-query';
import {alternativeFilters} from "../../../views/caremgmt/worklist/constants";
import {PatientSearchDTO} from "../../../dtos/PatientSearchDTO";
import {SessionStorageUtil} from '../../../utils/localStorageUtil';

const CustomWorklistContext = createContext();

const CustomWorklistProvider = ({children}) => {
  const globalDispatch = useDispatch();
  const [state, dispatch] = useReducer(reducer, initialState);

  const getters = getGetters(state);
  const toggles = getToggles(dispatch);
  const setters = getSetters(dispatch);
  const requestedWorklistId = state?.getCustomWorklistRequest?.worklistId;

  const {refetch: getCustomWorklistAsync} = useQuery({
    queryKey: ['getCustomWorklist'],
    queryFn: () => {
      const {worklistId, activeRole} = state?.getCustomWorklistRequest || {};

      getCustomWorklist(worklistId)
      .then((response) => {
        setters.setCustomWorklist(response.data);

        const {quickList, patientRequestDTO} = response.data;
        const {
          filterParams,
          sortParams,
          pageNumber,
          pageSize,
          previousTotalSize
        } = patientRequestDTO;
        let updatedFilterParams = [...filterParams]
        let updatedPageNumber = pageNumber
        let updatedSortParams = sortParams

        const sessionObj = SessionStorageUtil.getObj('lastMembersQueryValues') || null
        if (SessionStorageUtil.getObj('backing') && sessionObj) {
          const payload = {...JSON.parse(sessionObj)}
          updatedPageNumber = payload.pageNumber
          setters.setCustomFilters(payload.filterParams)
          updatedFilterParams = [...payload.filterParams]
        } else {
          setters.setCustomFilters(filterParams);
        }
        const invert = Object.fromEntries(
          Object.entries(alternativeFilters).map(([k, v]) => [v, k]));
        const newFilters = updatedFilterParams.map(f =>
          invert[f.paramName]
            ? {...f, paramName: invert[f.paramName]}
            : f
        )

        const patientSearch = new PatientSearchDTO(
          activeRole?.leadId ? [] : newFilters,
          updatedSortParams || [],
          updatedPageNumber || 1,
          pageSize || 20,
          quickList,
          previousTotalSize
        );
        setters.setPatientSearch({...patientSearch, type: worklistId})
      })
    },
    refetchOnWindowFocus: false,
    manual: true,
    enabled: false
  })

  useEffect(() => {
    if (requestedWorklistId) {
      getCustomWorklistAsync()
    }
  }, [getCustomWorklistAsync, requestedWorklistId])

  const setCustomWorkListError = (errorInfo) => {
    setters.setCustomWorkListError(errorInfo);
  }

  const callbacks = {
    setCustomWorkListError: setCustomWorkListError,
    saveCustomWorklist: async (quickListName, name, notes, patientRequest) => {
      setCustomWorkListError([]);

      return saveCustomWorklistRequest(quickListName, name, notes, patientRequest)
        .then(() => {
          setters.saveAsCustomWorklistRequest({quickListName, name, notes, patientRequest});
          globalDispatch(loadCustomFilteredList());
          toggles.toggleSaveAsCustomWorklist(false);
          return true;
        })
        .catch(err => {
          const errorMessage = err?.response?.data?.message;
          if (errorMessage === 'CUSTOM_WORKLIST_NAME_ALREADY_EXISTS') {
            setCustomWorkListError(["Error", "A worklist with this name already exists"]);
          } else {
            setCustomWorkListError(["Error", errorMessage || err?.message || "Unable to save custom worklist"]);
          }
          return false;
        })
    },
    deleteCustomWorklist: async (worklistId) => await deleteCustomWorklist(
      worklistId)
    .then(() => {
      toggles.toggleDeleteCustomWorklist(false);
    }),
    getCustomWorklist: async (worklistId, activeRole) => {
      setters.setGetCustomWorklistRequest({worklistId, activeRole})
    }
  }

  const value = useMemo(() => ({
    ...getters,
    ...toggles,
    ...setters,
    ...callbacks
  }), [callbacks, getters, setters, toggles]);

  return (
    <CustomWorklistContext.Provider value={value}>
      {children}
    </CustomWorklistContext.Provider>
  )
};
const useCustomWorklistContext = () => useContext(CustomWorklistContext);

CustomWorklistProvider.propTypes = {
  children: PropTypes.node.isRequired,
};

export {
  CustomWorklistProvider,
  useCustomWorklistContext,
}
